'use client';

import * as React from 'react';
import { ArrowRightLeftIcon, LandmarkIcon, ReceiptIcon } from 'lucide-react';
import { motion } from 'motion/react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@workspace/ui/components/card';
import { cn } from '@workspace/ui/lib/utils';

const DATA_MATCHING = [
  { icon: LandmarkIcon, label: 'Bank' },
  { icon: ReceiptIcon, label: 'Beleg' }
];

const MotionCard = motion.create(Card);

export function BentoBankMatchingCard({
  className,
  ...other
}: React.ComponentPropsWithoutRef<typeof MotionCard>): React.JSX.Element {
  const [active, setActive] = React.useState<number>(0);

  // Simple toggle effect to simulate matching occurring
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev === 0 ? 1 : 0));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <MotionCard
      className={cn(
        'relative h-[300px] max-h-[300px] overflow-hidden',
        className
      )}
      {...other}
    >
      <CardHeader>
        <CardTitle className="text-xl font-semibold">Bankabgleich</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="line-clamp-2 text-sm text-muted-foreground">
          Zahlungen und Belege werden automatisch erkannt und verknüpft.
        </p>
        <div
          aria-hidden="true"
          className="pointer-events-none relative h-[142px] flex-auto select-none overflow-hidden"
        >
          <div className="relative flex h-full flex-col items-center justify-center">
            {/* Rings */}
            <div className="absolute blur-[1px]">
              <div className="absolute left-1/2 top-1/2 ml-[calc(-216/2/16*1rem)] mt-[calc(-216/2/16*1rem)] size-[calc(216/16*1rem)] rounded-full border opacity-60 dark:opacity-100" />
              <div className="absolute left-1/2 top-1/2 ml-[calc(-280/2/16*1rem)] mt-[calc(-280/2/16*1rem)] size-[calc(280/16*1rem)] rounded-full border opacity-50 dark:opacity-90" />
              <div className="absolute left-1/2 top-1/2 ml-[calc(-344/2/16*1rem)] mt-[calc(-344/2/16*1rem)] size-[calc(344/16*1rem)] rounded-full border opacity-40 dark:opacity-80" />
            </div>

            {/* Icons */}
            <div className="flex flex-row gap-8 items-center relative z-10 sm:gap-12">
              <div
                className={cn(
                  'transition-all duration-1000 ease-in-out transform',
                  active === 0 ? 'scale-110 opacity-100' : 'scale-100 opacity-70'
                )}
              >
                <div className="size-10 rounded-full bg-background border-2 border-primary/20 ring-4 ring-primary/5 flex items-center justify-center shadow-lg sm:size-12">
                  <LandmarkIcon className="size-5 text-primary sm:size-6" />
                </div>
                <div className="text-[10px] text-center mt-1.5 font-medium text-muted-foreground sm:text-xs sm:mt-2">Bank</div>
              </div>

              <div className="relative">
                <ArrowRightLeftIcon className={cn(
                  "size-5 text-primary transition-all duration-500 sm:size-6",
                  active === 1 ? "opacity-100 scale-125" : "opacity-50 scale-100"
                )} />
              </div>

              <div
                className={cn(
                  'transition-all duration-1000 ease-in-out transform',
                  active === 1 ? 'scale-110 opacity-100' : 'scale-100 opacity-70'
                )}
              >
                <div className="size-10 rounded-full bg-background border-2 border-primary/20 ring-4 ring-primary/5 flex items-center justify-center shadow-lg sm:size-12">
                  <ReceiptIcon className="size-5 text-primary sm:size-6" />
                </div>
                <div className="text-[10px] text-center mt-1.5 font-medium text-muted-foreground sm:text-xs sm:mt-2">Beleg</div>
              </div>
            </div>

            {/* Connection Animation */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={cn(
                "absolute w-32 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent transition-opacity duration-1000",
                active === 1 ? "opacity-100" : "opacity-0"
              )} />
            </div>

            {/* Status Text */}
            <div className="absolute bottom-2">
              <div className={cn(
                "text-xs font-medium px-3 py-1 rounded-full transition-colors duration-500",
                active === 1 ? "bg-primary/10 text-primary" : "bg-secondary text-secondary-foreground"
              )}>
                {active === 1 ? "Match gefunden!" : "Suche..."}
              </div>
            </div>

          </div>
        </div>
      </CardContent>
    </MotionCard>
  );
}
